import { Icon, Li, ListHeading, Router } from '@startlibs/components';
import { Routes, Route,useLocation, useNavigate } from '@startlibs/components';
import { useToggle } from '@startlibs/core';
import React, {useContext, useEffect, useState} from 'react'
import {AdminControlPanel} from './AdminControlPanel'
import {AdminCreateRequest} from './AdminCreateRequest'
import {AdminDashboard} from './AdminDashboard'
import {AdminExpertsRoutes} from './AdminExpertsRoutes'
import {AdminRequest} from './AdminRequest'
import {DIVISION, OPERATOR, SUPER} from '../enums/UserRole';
import {InfoBox} from '../components/InfoBox'
import {NavbarTabs, UserNavbar} from '../components/Navbar';
import {PageLoadingSuspense} from '../components/PageLoading'
import {ProviderTitle} from '../components/ProviderTitle'
import {SuspensePage} from '../components/SuspensePage'
import {lazyMaybeUserInfo, lazyProviderInfo} from '../components/WithProvider'
import {setJwt} from '../hooks/useJwt';
import {AdminLogin} from './AdminLogin'
import { useIntl } from 'react-intl';
import { AdminSSOLogin } from './AdminSSOLogin';
import {Redirect} from "../components/Redirect";
import { preventDefault } from '@startlibs/utils';
import { confirmExitPage } from '../hooks/useConfirmExitPage';
import { DicomRouterProvider } from '../utils/DicomRouterContext';
import { AdminCreateRequestNew } from './AdminCreateRequestNew';
import {LocaleContext} from "../components/I18NProvider";
import {getDefaultLanguageInfo} from "../utils/getInitLocale";

// JWT for development environment only (localhost) - loaded from .env file
const DEV_JWT = import.meta.env.DEV && import.meta.env.VITE_DEV_JWT_ADMIN
const getJwt = () => window.JWT_URL_PARAM || window.localStorage.getItem("admin-jwt") || DEV_JWT // Comment/Uncomment this to automatic login admin

export const AdminRoutes = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [divisionsFilter, setDivisionsFilter] = useState()
  
  const providerInfo = lazyProviderInfo.read()
  
  if (getJwt()) {
    setJwt(getJwt(),"admin")
  }

  const toggle = useToggle(true)
  const intl = useIntl()
  const {locale, changeLanguage} = useContext(LocaleContext)
  useEffect(() => {
    const languageInfo = getDefaultLanguageInfo(providerInfo)
    if (languageInfo && locale !== languageInfo.id) {
      changeLanguage(languageInfo.id)
    }
  }, []);

  if (location.pathname === "/admin/login") {
    return <AdminLogin />
  }

  if ((location.pathname === "/admin/sso/login") && (providerInfo?.ssoEnabled === true || providerInfo?.ssoAdminEnabled === true)){
    return <AdminSSOLogin/>
  }

  const redirectToLogin = () => (providerInfo?.ssoEnabled === true || providerInfo?.ssoAdminEnabled === true)
    ?
    <Redirect to="/admin/sso/login" />
    :
    <Redirect to="/admin/login" />

  if (!getJwt()) {
    return redirectToLogin()
  }

  const userInfo = lazyMaybeUserInfo.read()
  if (!userInfo) {
    return redirectToLogin()
  }

  // If a page has useSetConfirmExitPage and we add this confrimation on redirects, then it will be asked to confirm exit
  const confirmRedirect = preventDefault((e) => {
    // const href = e.target.href // Not work in this new
    // const href = e.target.getAttribute('href') // Works
    const href = e.target.pathname // Works and already used in other part of system
    confirmExitPage().then(() => navigate(href))
  })

  return <>
    {
      toggle.isOpen && userInfo.role === DIVISION && !userInfo.categories?.length &&
      <InfoBox noDivisionsNotice lightYellow flex>
        You are no longer tied to any division. <br/>
        Please contact a super administrator.
        <Icon icon="x" onClick={toggle.close} />
      </InfoBox>
    }
    <UserNavbar
      indexUrl="/admin"
      resetPasswordUrl="/api/admin/resetPassword"
      subtitle="Administrator"
      exitUrl='/admin'
      routes={<>
        <NavbarTabs onClick={confirmRedirect} selected={location.pathname === "/admin" || location.pathname === "/admin/"} to="/admin">Cases</NavbarTabs>
        <NavbarTabs onClick={confirmRedirect} selected={location.pathname === "/admin/experts" || location.pathname === "/admin/experts/"} to="/admin/experts">Experts</NavbarTabs>
      </>}
      extraContextMenuItem={(userInfo) => userInfo.role !== OPERATOR && [
        <ListHeading><span>Institution settings</span></ListHeading>,
        <Li label={intl.formatMessage({defaultMessage:"Staff management", description:"Staff management label"})} icon="users-line" key="staff" linkTo="/admin/control/staff"/>,
        <Li label="Division settings" icon="divisions" key="divisions" linkTo="/admin/control/divisions"/>,
        userInfo.role === SUPER && <Li label="Notification settings" icon="mail-line" key="notifications" linkTo="/admin/control/notifications"/>,
        userInfo.role === SUPER && <Li label="Data export" icon="csv" key="export" linkTo="/admin/control/export"/>,
        userInfo.role === SUPER && <Li label="Analytics" icon="analytics" key="analytics" linkTo="/admin/control/analytics"/>,
        userInfo.role === SUPER && <Li label="Accepted jurisdictions" icon="jurisdictions" key="jurisdictions" linkTo="/admin/control/jurisdictions"/>,
        userInfo.role === SUPER && <Li label="Payment fees" icon="payment" key="paymentfees" linkTo="/admin/control/paymentfees"/>,
        userInfo.role === SUPER && <Li label="Audit log" icon="audit" key="audit" linkTo="/admin/control/audit"/>,
        userInfo.role === SUPER && <Li label="Intake form settings" icon="intake-forms" key="intake-forms" linkTo="/admin/control/intake-forms"/>,
        userInfo.role === SUPER && <Li label="Case report settings" icon="report" key="case-report" linkTo="/admin/control/case-report"/>,
        <ListHeading><span>Your profile</span></ListHeading>
      ].filter(Boolean)}
    />
    <ProviderTitle/>
    <PageLoadingSuspense>
      <DicomRouterProvider>
        <Router>
          <Routes >
            <Route path="admin/create" element={<SuspensePage Component={AdminCreateRequestNew} />}/>
            <Route path="admin/create-request" element={<SuspensePage Component={AdminCreateRequest} />}/>
            <Route path="admin/request/:requestCode" element={<SuspensePage Component={AdminRequest}/>}/>
            <Route path="admin/request/:requestCode/*" element={<SuspensePage Component={AdminRequest}/>}/>
            <Route path="admin/experts*" element={<AdminExpertsRoutes/>}/>
            <Route path="admin/experts/*" element={<AdminExpertsRoutes/>}/>
            {userInfo.role !== OPERATOR && <Route path="/admin/control/*" element={<AdminControlPanel />}/>}
            <Route path="*" element={<SuspensePage Component={AdminDashboard} default divisionsFilter={divisionsFilter} setDivisionsFilter={setDivisionsFilter}/>}/>
          </Routes>      
        </Router>
      </DicomRouterProvider>
    </PageLoadingSuspense>
  </>
}
